import {
  CreateTask,
  PatchScheduleTask,
  PatchTask,
  Task,
} from "@coworker/apprestructured/src/task-management/types/Task";
import TaskState from "@coworker/apprestructured/src/task-management/types/TaskState";
import { SignedUpload } from "@coworker/apprestructured/src/task-management/types/Upload";
import { httpClient } from "@coworker/http-client";
import { Comment } from "../types/Comment";
import { Log } from "../types/Log";
import { Schedule } from "../types/Schedule";
import { ItemNoAndType } from "../types/TaskDetailTypes";

export async function createTask(createTask: CreateTask) {
  return httpClient.tasks.post<Task>("/tasks-v2", { body: createTask });
}

export async function createTasksBatch(tasks: CreateTask[]) {
  return httpClient.tasks.post<Task[]>("/tasks-v2/batch", { body: tasks });
}

export async function getTask(id: string) {
  const tasks = await httpClient.tasks.get<Task[]>(`/tasks-v2/${id}`);
  return Array.isArray(tasks) ? tasks[0] : undefined;
}

export async function updateTask(id: string, patchTask: PatchTask) {
  return httpClient.tasks.patch<Task>(`/tasks-v2/${id}`, { body: patchTask });
}

export type BulkUpdateTaskV2 = { id: string; patch: PatchTask };
export async function updateTasksV2({ tasks }: { tasks: BulkUpdateTaskV2[] }) {
  return httpClient.tasks.patch<Task[]>("/tasks-v2/bulk-update", {
    body: { tasks },
  });
}

export type BulkUpdateTaskV1Item = { task_id: string; patch: object };
type BulkUpdateTasksV1Response = { success: boolean; tasks: any[] };
export async function updateTasksV1(tasks: BulkUpdateTaskV1Item[]) {
  return await httpClient.tasks.put<BulkUpdateTasksV1Response>(
    "/v1/tasks/bulk-update",
    { body: { tasks } }
  );
}

export async function updatePendingTasksInSchedule(
  scheduleId: string,
  patchScheduleTask: PatchScheduleTask
) {
  await httpClient.tasks.patch("/tasks-v2", {
    params: { schedule_id: scheduleId, state: "pending" },
    body: patchScheduleTask,
  });
}

export async function getTaskLogs(id: string) {
  return httpClient.tasks.get<Log[]>(`/tasks-v2/${id}/logs`);
}

export async function getTaskComments(id: string) {
  return httpClient.tasks.get<Comment[]>(`/tasks-v2/${id}/comments`);
}

export async function getTaskImages(taskId: string) {
  return httpClient.tasks.get<SignedUpload[]>(`/tasks-v2/${taskId}/images`);
}

export async function getTaskCommentsImages(taskId: string) {
  return httpClient.tasks.get<SignedUpload[]>(
    `/tasks-v2/${taskId}/comments/images`
  );
}

export async function getTaskFiles(taskId: string) {
  return httpClient.tasks.get<SignedUpload[]>(`/tasks-v2/${taskId}/files`);
}

export async function getTaskSchedule(taskId: string) {
  return httpClient.tasks.get<Schedule>(`/tasks-v2/${taskId}/schedule`);
}

export type TasksByTypeParams = {
  taskTypeId: string;
  storeId: string;
  state: TaskState;
};

export async function getMostRecentAssignedTask(
  taskTypeId: string,
  storeId: string,
  article?: ItemNoAndType
): Promise<Task | null> {
  return httpClient.tasks.get<Task | null>("/tasks-v2/last-assignee", {
    params: {
      task_type_id: taskTypeId,
      store_id: storeId,
      ...(article
        ? { article_no: article.no, article_type: article.type }
        : {}),
    },
  });
}

export async function getTasksByType(
  params: TasksByTypeParams
): Promise<Task[]> {
  return httpClient.tasks.get<Task[]>("/tasks-v2", {
    params: {
      task_type_id: params.taskTypeId,
      store_id: params.storeId,
      state: params.state,
    },
  });
}

type DetailsReferencePatchBody = {
  media_id: string;
  group_id?: string;
  article_id?: string;
  item_no?: string;
};

/**
 * Removes a media/group/item reference from pending v2 tasks whose
 * details.medias/details.items array points at it, e.g. after the
 * underlying media, group, or article has been deleted.
 * Endpoint: PATCH /tasks-v2/details-reference
 * Best-effort: errors are logged, never thrown, so callers can fire this
 * off without blocking the deletion it's cleaning up after.
 *
 * group_id/article_id/item_no must be omitted entirely when not
 * applicable — the API rejects an empty string ("") for these fields.
 * Some callers represent "no group" as group_id: "" rather than
 * undefined, so falsy values are stripped here rather than trusting
 * every call site to omit the key correctly.
 */
export async function removeTaskDetailsReference(
  body: DetailsReferencePatchBody,
  taskTypeIds: string[]
): Promise<void> {
  if (taskTypeIds.length === 0) {
    return;
  }

  const sanitizedBody: DetailsReferencePatchBody = { media_id: body.media_id };
  if (body.group_id) sanitizedBody.group_id = body.group_id;
  if (body.article_id) sanitizedBody.article_id = body.article_id;
  if (body.item_no) sanitizedBody.item_no = body.item_no;

  try {
    await httpClient.tasks.patch("/tasks-v2/details-reference", {
      params: { task_type_ids: taskTypeIds.join(",") },
      body: sanitizedBody,
    });
  } catch (error) {
    console.error("[removeTaskDetailsReference] API error:", error);
  }
}

export interface MediaItemTask {
  article_id: string;
  item_no: string;
}

/**
 * Get items with v2 tasks in a specific media
 * Endpoint: GET /tasks-v2/items?media_id=&task_type_ids=&state=
 * Returns: Array of { article_id, item_no } pairs
 */
export async function getMediaItemsWithTasks(
  mediaId: string,
  taskTypeIds: string[]
): Promise<MediaItemTask[]> {
  if (!mediaId || taskTypeIds.length === 0) {
    return [];
  }

  try {
    const response = await httpClient.tasks.get<
      Array<{ article_id: string; item_no: string }>
    >("/tasks-v2/items", {
      params: {
        media_id: mediaId,
        task_type_ids: taskTypeIds.join(","),
      },
    });

    if (Array.isArray(response)) {
      return response.map((item) => ({
        article_id: item.article_id,
        item_no: item.item_no,
      }));
    }
    return [];
  } catch (error) {
    console.error("[getMediaItemsWithTasks] API error:", error);
    throw error;
  }
}

/**
 * Get aggregated task counts per media in a store
 * Endpoint: GET /tasks-v2/count
 * Returns: Map of mediaId -> task count
 */
export async function getMediaTaskCountsInStore(
  taskTypeIds: string[]
): Promise<Record<string, number>> {
  if (taskTypeIds.length === 0) {
    return {};
  }

  try {
    const response = await httpClient.tasks.get<
      Array<{ media_id: string; tasks_count: number }>
    >("/tasks-v2/count", {
      params: {
        task_type_ids: taskTypeIds.join(","),
      },
    });

    // Convert array response to Record<mediaId, count>
    const result: Record<string, number> = {};
    if (Array.isArray(response)) {
      response.forEach(({ media_id, tasks_count }) => {
        result[media_id] = tasks_count;
      });
    }

    return result;
  } catch (error) {
    console.error("[getMediaTaskCountsInStore] API error:", error);
    throw error;
  }
}

/**
 * Fetch tasks by media ID, item number, and task type IDs
 * Uses direct JSONB query on tasks_v2 table
 */
/**
 * Fetch tasks by item number and task type IDs (across all medias)
 * Uses direct JSONB query on tasks_v2 table
 * No media filter - returns tasks from all medias containing this article
 */
export async function getTasksByItem(
  itemNo: string,
  storeId: string,
  taskTypeIds: string[],
  limit?: number,
  cursor?: string
): Promise<{
  tasks: Task[];
  lastSeenCreatedAt: string | null;
  lastSeenTaskId: string | null;
  totalCount: number;
}> {
  if (!itemNo || !storeId || taskTypeIds.length === 0) {
    return {
      tasks: [],
      lastSeenCreatedAt: null,
      lastSeenTaskId: null,
      totalCount: 0,
    };
  }

  try {
    const response = await httpClient.tasks.get<{
      tasks: Task[];
      total: number;
      pagination: {
        count: number;
        limit: number;
        hasMore: boolean;
        nextCursor: string | null;
      };
    }>("/tasks-v2/by-item", {
      params: {
        itemNo,
        storeId,
        taskTypeIds: taskTypeIds.join(","),
        limit: limit?.toString() || "10",
        ...(cursor && { cursor }),
      },
    });

    return {
      tasks: response?.tasks || [],
      lastSeenCreatedAt: response?.pagination?.nextCursor || null,
      lastSeenTaskId: null,
      totalCount: response?.total || 0,
    };
  } catch (error) {
    console.error("[getTasksByItem] API error:", error);
    throw error;
  }
}
